import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageTitle from '../../common/PageTitle';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { TableSkeleton } from '../../common/Skeletons';
import AwbLinkCell from '../../common/AwbLinkCell';
import { pageContainerSx, dataGridWrapSx, btnRowSx } from '../../styles/patterns';
import { formatDate, parseDateAndTime } from '../../helpers/UtilityHelper';
import {
  awbTextSx, cellStackSx, getStatusChipSx, primaryTextSx, secondaryTextSx, tertiaryTextSx,
} from '../orders/list2/cellStyles';
import FilterBar from '../appointments/shared/FilterBar';
import ExportButton from '../appointments/shared/ExportButton';
import useAppointmentSearch from '../appointments/shared/useAppointmentSearch';
import { PTL_PATHS, errorText, nonAbdTodayFilter } from './constants';

const columns = [
  {
    field: 'awb', headerName: 'AWB / Reference', minWidth: 200, flex: 1.2, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <AwbLinkCell awb={row.awb_number} fallback='-' sx={{ ...awbTextSx, whiteSpace: 'nowrap' }} />
        {row.reference && <Typography sx={tertiaryTextSx}>Ref: {row.reference}</Typography>}
      </Box>
    ),
  },
  {
    field: 'edd', headerName: 'EDD', minWidth: 150, flex: 0.9, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <Typography sx={primaryTextSx}>{row.edd ? parseDateAndTime(row.edd) : '-'}</Typography>
      </Box>
    ),
  },
  {
    field: 'orderStatus', headerName: 'Status', minWidth: 160, flex: 1, sortable: false,
    renderCell: ({ row }) => (row.orderStatus ? <Chip size='small' label={row.orderStatus} sx={getStatusChipSx(row.orderStatus)} /> : '-'),
  },
  {
    field: 'courier', headerName: 'Courier', minWidth: 150, flex: 1, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <Typography sx={primaryTextSx}>{row.courierDetail?.parent || '-'}</Typography>
        {row.courierDetail?.name && <Typography sx={tertiaryTextSx}>{row.courierDetail.name}</Typography>}
      </Box>
    ),
  },
  {
    field: 'lane', headerName: 'Pickup → Drop', minWidth: 200, flex: 1.3, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <Typography sx={secondaryTextSx}>
          {row.pickup_details?.from_city || '-'} → {row.delivery_details?.to_city || '-'}
        </Typography>
        <Typography sx={tertiaryTextSx}>
          {row.pickup_details?.from_pincode || ''} → {row.delivery_details?.to_pincode || ''}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'awbRegisteredDate', headerName: 'AWB Date', minWidth: 140, flex: 0.8, sortable: false,
    renderCell: ({ row }) => (
      <Typography sx={secondaryTextSx}>{row.awbRegisteredDate ? parseDateAndTime(row.awbRegisteredDate) : '-'}</Typography>
    ),
  },
];

/** Non-ABD PTL shipments with EDD today; opened from the PTL Delivery Health card (same filter as its count). */
export default function NonAbdToday() {
  const [now] = useState(() => new Date());
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(20);
  const [reload, setReload] = useState(0);
  const todayFilter = nonAbdTodayFilter(now);

  const { data, isFetching, error } = useAppointmentSearch({
    filters, extraAndFilter: todayFilter, page, sizePerPage, reload, appointmentRequired: false,
    sort: { field: 'edd', direction: 'ASC' },
  });
  const rows = data?.data || [];

  const apply = (f) => { setFilters({ ...f, fromDate: null, toDate: null }); setPage(0); setReload((r) => r + 1); };

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Non-ABD PTL shipments with their EDD today'>Non-ABD Shipments Due Today</PageTitle>

      <Box sx={{ ...btnRowSx, justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={btnRowSx}>
          <Button component={Link} to={PTL_PATHS.today} size='small' startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>
            PTL Delivery Health
          </Button>
          <Chip size='small' color='primary' variant='outlined' sx={{ fontWeight: 600 }}
            label={`EDD today · ${formatDate(now, 'dd MMM yyyy')}`} />
        </Box>
        <ExportButton label='Export' filters={filters} extraAndFilter={todayFilter}
          appointmentRequired={false} fileName='PTL_NonABD_Due_Today' />
      </Box>

      <FilterBar
        show={['courier', 'awb', 'city']}
        initial={{ fromDate: null, toDate: null }}
        onApply={apply}
        onReset={() => apply({})}
      />

      {error ? (
        <Alert severity='error'>{errorText(error, 'Could not load the shipments. Please refresh.')}</Alert>
      ) : isFetching && !rows.length ? (
        <TableSkeleton rows={6} columns={columns.length} />
      ) : (
        <Box sx={dataGridWrapSx}>
          <ProzoDataGrid
            columns={columns}
            rows={rows}
            rowCount={data?.count || 0}
            paginationMode='server'
            pagination
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={(s) => { setSizePerPage(s); setPage(0); }}
            loading={isFetching}
            getRowId={(r) => r.id || r.awb_number}
            boxShadow={0}
          />
        </Box>
      )}
    </Box>
  );
}
